import { OidcAccessToken, OidcTokenStorage } from '@ori/auth';

declare global {
    interface Token {
        Token: string;
    }

    interface TokenService {
        getToken: () => Promise<Token | null>;
        getPublicToken: () => Promise<Token | null>;
    }

    interface Ori {
        tokenService?: TokenService;
    }

    interface Window {
        ori?: Ori;
    }
}

class TokenProvider {
    private readonly oidcTokenStorage: OidcTokenStorage = new OidcTokenStorage();

    async getAuthorizationToken(): Promise<string> {
        let token: string | null = await this.getTokenServiceUserToken();

        if (!token) {
            token = this.getSessionStorageToken();
        }

        if (!token) {
            token = await this.getTokenServicePublicToken();
        }

        if (!token) {
            throw new Error('Missing access token');
        }

        return token;
    }

    async getCustomerId(): Promise<string | null> {
        try {
            const token = await this.getToken();

            return token.getClaim('customer_id');
        } catch {
            return null;
        }
    }

    private async getToken(): Promise<OidcAccessToken> {
        if (window.ori?.tokenService) {
            const oriToken = await window.ori.tokenService.getToken();

            if (oriToken) {
                return new OidcAccessToken(oriToken.Token);
            }
        }

        const token: OidcAccessToken | null = this.oidcTokenStorage.getAccessToken();
        if (token !== null) {
            return token;
        }

        throw new Error('Missing auth token');
    }

    // eslint-disable-next-line class-methods-use-this -- required for tokenService
    private async getTokenServiceUserToken(): Promise<string | null> {
        if (window.ori?.tokenService) {
            try {
                const oriToken = await window.ori.tokenService.getToken();

                if (oriToken) {
                    return oriToken.Token;
                }
            } catch {
                // silence is golden
            }
        }

        return null;
    }

    private getSessionStorageToken(): string | null {
        const token = this.oidcTokenStorage.getAccessToken();

        if (token !== null) {
            return token.toString();
        }

        return null;
    }

    // eslint-disable-next-line class-methods-use-this -- required for tokenService
    private async getTokenServicePublicToken(): Promise<string | null> {
        if (window.ori?.tokenService) {
            try {
                const oriToken = await window.ori.tokenService.getPublicToken();

                if (oriToken) {
                    return oriToken.Token;
                }
            } catch {
                // silence is golden
            }
        }

        return null;
    }
}

const tokenProvider = new TokenProvider();

export { tokenProvider as TokenProvider };
